// Pillar D — one row in the post-Customer-Merge duplicate-Patient-groups
// list. Figma: collapsed "To merge"/"Conflict" states (node 11427:30977),
// expanded inline form (node 11435:29170).
import React, { FC } from "react";
import { useIntl } from "react-intl";
import Box from "../../Unknown/Box";
import Paper from "../../Unknown/Paper";
import Typography from "../../Unknown/Typography";
import Button from "../../Unknown/Button";
import Alert from "../../Unknown/Alert";
import AlertTitle from "../../Unknown/AlertTitle";
import CircularProgress from "../../Unknown/CircularProgress";
import Radio from "../../Unknown/Radio";
import RadioGroup from "../../Unknown/RadioGroup";
import FormControlLabel from "../../Unknown/FormControlLabel";
import PatientMergeColumn, {
  getPatientMergeGridRowCount,
} from "../../Patient/MergePatients/PatientMergeColumn";
import {
  PatientInMerge,
  PatientFieldSelections,
  PatientMergeFieldKey,
} from "../../Patient/MergePatients/types";
import patientMergeMessages from "../../Patient/MergePatients/messages";
import { DuplicatePatientGroupOutcome } from "./types";
import { DuplicatePatientGroup } from "./useDuplicatePatientGroups";
import messages from "./messages";

type DuplicatePatientGroupCardProps = {
  group: DuplicatePatientGroup;
  patients: PatientInMerge[] | undefined;
  hasConflict: boolean;
  // AC37/38 — appointment ids involved in the overlap, one "View appointment
  // N" link each (Figma node 11515:29993), opening in a new tab.
  conflictingAppointmentIds: string[];
  isExpanded: boolean;
  isAnyExpanded: boolean;
  isSubmitting: boolean;
  outcome: DuplicatePatientGroupOutcome | undefined;
  fieldSelections: PatientFieldSelections;
  relationship: "ward" | "family" | null;
  fieldKeys: PatientMergeFieldKey[];
  canSubmit: boolean;
  customerName: string;
  onExpand: () => void;
  onFieldSelect: (field: PatientMergeFieldKey, patientId: string) => void;
  onSetRelationship: (relationship: "ward" | "family") => void;
  onSubmit: () => void;
  onPatientClick: (mrn: string) => void;
  onAppointmentClick: (appointmentId: string) => void;
};

const DuplicatePatientGroupCard: FC<DuplicatePatientGroupCardProps> = ({
  group,
  patients,
  hasConflict,
  conflictingAppointmentIds,
  isExpanded,
  isAnyExpanded,
  isSubmitting,
  outcome,
  fieldSelections,
  relationship,
  fieldKeys,
  canSubmit,
  customerName,
  onExpand,
  onFieldSelect,
  onSetRelationship,
  onSubmit,
  onPatientClick,
  onAppointmentClick,
}) => {
  const { formatMessage } = useIntl();

  if (outcome?.status === "success") {
    return (
      <Alert severity="success">
        <Typography variant="body2">
          {formatMessage(messages.duplicateGroupMergedSuccess, {
            name: outcome.patientName,
          })}
        </Typography>
        <Button
          variant="text"
          size="small"
          onClick={() => onPatientClick(outcome.patientMrn)}
          sx={{
            p: 0,
            minWidth: 0,
            color: "inherit",
            textTransform: "none",
            textDecoration: "underline",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {formatMessage(messages.duplicateGroupViewPatient)}
        </Button>
      </Alert>
    );
  }

  if (outcome?.status === "error") {
    return (
      <Alert severity="error">
        <AlertTitle>{outcome.title}</AlertTitle>
      </Alert>
    );
  }

  if (isExpanded && isSubmitting) {
    return (
      <Paper variant="outlined">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 360,
            gap: 5,
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="body1" textAlign="center">
            {formatMessage(patientMergeMessages.processing)}
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (isExpanded && patients) {
    return (
      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        <Box
          sx={{
            display: "grid",
            gridAutoFlow: "column",
            gridTemplateColumns: `repeat(${patients.length}, minmax(360px, 1fr))`,
            gridTemplateRows: `repeat(${getPatientMergeGridRowCount(
              fieldKeys,
            )}, auto)`,
            overflowX: "auto",
            overflowY: "hidden",
            columnGap: "1px",
            backgroundColor: "rgba(0, 0, 0, 0.23)",
          }}
        >
          {patients.map((patient) => (
            <PatientMergeColumn
              key={patient.id}
              patient={patient}
              fieldSelections={fieldSelections}
              onFieldSelect={onFieldSelect}
              showRemove={false}
              onRemove={() => {}}
              onPatientClick={onPatientClick}
              fieldKeys={fieldKeys}
            />
          ))}
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            px: "16px",
            py: "16px",
            borderTop: "1px solid",
            borderColor: "rgba(0, 0, 0, 0.23)",
          }}
        >
          <RadioGroup
            row
            value={relationship ?? ""}
            onChange={(e) =>
              onSetRelationship(e.target.value as "ward" | "family")
            }
            sx={{ gap: 3 }}
          >
            <FormControlLabel
              value="ward"
              control={<Radio />}
              label={formatMessage(messages.duplicateGroupIsWardOf, {
                customerName,
              })}
            />
            <FormControlLabel
              value="family"
              control={<Radio />}
              label={formatMessage(messages.duplicateGroupIsFamilyMemberOf, {
                customerName,
              })}
            />
          </RadioGroup>

          <Button variant="contained" onClick={onSubmit} disabled={!canSubmit}>
            {formatMessage(messages.duplicateGroupMergeButton)}
          </Button>
        </Box>
      </Paper>
    );
  }

  // Collapsed states — "To merge" or "Conflict" (Figma component set 11427:30977).
  const isLoading = !patients;
  const mergeNowDisabled = isLoading || isAnyExpanded || hasConflict;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 3,
        p: 3,
        border: "1px solid",
        borderColor: "rgba(0, 0, 0, 0.23)",
        borderRadius: 1,
      }}
    >
      <Box display="flex" gap={5} sx={{ flexShrink: 0 }}>
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textTransform: "uppercase", display: "block" }}
          >
            {formatMessage(messages.duplicateGroupName)}
          </Typography>
          <Typography variant="body2">
            {group.lastName}, {group.firstName}
          </Typography>
        </Box>
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textTransform: "uppercase", display: "block" }}
          >
            {formatMessage(messages.duplicateGroupDateOfBirth)}
          </Typography>
          <Typography variant="body2">{group.dateBirth ?? "—"}</Typography>
        </Box>
      </Box>

      {!hasConflict && (
        <Button
          variant="contained"
          onClick={onExpand}
          disabled={mergeNowDisabled}
        >
          {formatMessage(messages.duplicateGroupMergeNow)}
        </Button>
      )}

      {hasConflict && (
        // Figma node 11515:29993 — alert sits beside Name/DOB, not below it.
        <Alert severity="error" sx={{ width: "50%" }}>
          <AlertTitle>
            {formatMessage(messages.duplicateGroupConflictTitle)}
          </AlertTitle>
          <Typography variant="body2" component="span">
            {formatMessage(messages.duplicateGroupConflictDescription)}
          </Typography>
          {/* AC37/38 — one "View appointment N" link per conflicting
              appointment (Figma node 11515:29993), opens in a new tab. */}
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {conflictingAppointmentIds.map((appointmentId, index) => (
              <li key={appointmentId}>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => onAppointmentClick(appointmentId)}
                  sx={{
                    p: 0,
                    minWidth: 0,
                    textDecoration: "underline",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {formatMessage(messages.duplicateGroupViewAppointment, {
                    index: index + 1,
                  })}
                </Button>
              </li>
            ))}
          </Box>
        </Alert>
      )}
    </Box>
  );
};

export default DuplicatePatientGroupCard;
